import { getFormattedDueDate as formatDueDate } from '@eventbrite/event-date-time';
import { PrivateSetting } from '../../constants';
import { EventStatus } from '../../constants/eventStatus';
import { isMyOrderFlow as getIsMyOrderFlow } from '../../selectors/app';
import { CheckoutState } from '../../state';
import { createSelector, getNamespaceValue } from '../utils/selector';

export const getCheckoutGroups = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'checkoutInfo.checkoutGroups', []);

export const getCurrency = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'currency');

export const getEventCountryCode = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'venue.address.country', '');

export const getEventVenue = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'venue');

export const getCategory = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'category');

export const getSubcategory = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'subcategory');

export const getEventGroupSettings = (state: CheckoutState) => {
    const {
        teamPasswordIncluded: hasPassword,
        teamPasswordRequired: isPasswordRequired,
        teamPstIncluded: hasPreferredStartTime,
        teamPstRequired: isPreferredStartTimeRequired,
        teamPstChoices: preferredStartTimeChoices,
    } = getNamespaceValue(state.event, 'groupSettings') || {
        hasPassword: null,
        isPasswordRequired: null,
        hasPreferredStartTime: null,
        isPreferredStartTimeRequired: null,
        preferredStartTimeChoices: null,
    };

    return {
        hasPassword,
        isPasswordRequired,
        hasPreferredStartTime,
        isPreferredStartTimeRequired,
        preferredStartTimeChoices,
    };
};

/**
 * Selector that can be used to return the group type in case of group registration
 */
export const getEventGroupType = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'groupSettings.groupType');

export const getEventId = (state: CheckoutState) =>
    getNamespaceValue(state?.event, 'id');

export const getEventImageSrc = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'properties.image') ?? null;

export const getEventOrganizationId = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'organization.id');

export const getEventOrganizationName = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'organization.name');

export const getEventSalesStatus = (state: CheckoutState) => ({
    eventSalesStatus: getNamespaceValue(state.event, 'eventSalesStatus'),
});

export const getEventSeatMapThumbnailSrc = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'properties.seatmapThumbnailUrl');

export const getEventShippingSettings = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'shippingSettings');

export const getEventStatus = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'status');

export const getEventTimezone = (state: CheckoutState) =>
    getNamespaceValue(state.event.start, 'timezone');

export const getEventTitle = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'name.text', '');

export const getEventUrl = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'url');

// TODO: move this selector to app seletors file
/**
 * Selector that can be used to retrieve the due payment date string to display
 */
export const getFormattedDueDate = createSelector(
    (state: CheckoutState) => state.app.locale,
    (state: CheckoutState) => state.app.deferredPaymentDueDays,
    ((userLocale: string, deferredPaymentDueDays: number) =>
        formatDueDate({
            locale: userLocale,
            deferredPaymentDueDays,
        })) as unknown as any,
);

export const getHasPromoCode = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'shouldShowPromoCodeLink', false);

export const getIsDraftEvent = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'status') === EventStatus.draft;

export const getIsEventOnline = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'onlineEvent', false);

/**
 * For online events rework MVP, the taxes are TBD until the attendee enters their address
 * TODO: Finish this method in EB-272601 and remove this comment
 * This values should come from the BE and should be false in several situations
 * - The creator is an NPO based on their responses in the the Tax Settings questions
 * - The taxes are absorbed by the creator
 */
export const getAreTaxesCalculatedInCheckout = (_: CheckoutState) => true;

/**
 * Selector that can be used to know if the current event is protected
 * An event is considered protected if isProtected is true, and privacySetting equals invite_only or password_protected
 * Password_expired is a special case that exists only on UI side: it is not returned by the BE, and is set via UPDATE_EVENT_PRIVACY_SETTING action when the event's password
 * is either changed by the organizer while the user is buying, or when the password has expired.
 */
export const getIsEventProtected = (state: CheckoutState) => {
    const isMyOrderFlow = getIsMyOrderFlow(state);
    const privacySetting = getNamespaceValue(state.event, 'privacySetting');
    const isProtected = getNamespaceValue(
        state.event,
        'properties.isProtected',
    );

    return Boolean(
        isProtected &&
            [
                PrivateSetting.passwordExpired,
                PrivateSetting.passwordRequired,
                PrivateSetting.invitationRequired,
            ].includes(privacySetting) &&
            !isMyOrderFlow,
    );
};

/**
 * This event country is determined by the Venue country.
 * If the event doesn't have a venue (online events) this country is taken from
 * the creator's payout country.
 * More details in https://github.com/eventbrite/core/blob/master/django/src/www/eventbrite/ebapps/events/models/event.py
 */
export const getEventCountry = (state: CheckoutState) =>
    getNamespaceValue(state.app, 'defaultUserCountry');

export const getIsFreeEvent = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'isFree');

export const getEventStartDate = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'start');

export const getEventEndDate = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'end');

// TODO: move this selector to group registration seletors file
export const getIsNewlyCreatedGroup = (state: CheckoutState) =>
    getNamespaceValue(
        state.groupRegistration,
        'teamSettings.isNewlyCreatedGroup',
    );

/**
 * Selector that can be used to retrieve the event's reserved seating prop
 */
export const getIsReservedSeating = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'isReservedSeating', false);

/**
 * @return `true` when waitlist is enabled and available for new entries, regardless of waitlist type.
 */
export const getIsWaitlistAvailable = (state: CheckoutState) =>
    !getNamespaceValue(state.event, 'waitlistSettings.full', true) ?? false;

export const getSurveyType = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'surveyType');

// TODO: move this selector to group registration seletors file
export const getTeamSettings = (state: CheckoutState) =>
    getNamespaceValue(state.groupRegistration, 'teamSettings');

// TODO: move this selector to group registration seletors file
export const getTeamSettingsErrors = (state: CheckoutState) =>
    getNamespaceValue(state.groupRegistration, 'teamSettings.errors');

export const getTicketReseller = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'ticketReseller');

export const getWaitlistSettings = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'waitlistSettings');

export const getWaitlistTicketId = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'waitlistSettings.ticketId') ?? null;

export const hasSpecialInstructions = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'surveyInfo');

export const isDonation = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'properties.isDonation');

export const isListedEvent = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'listed');

export const isLockedEvent = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'isLocked');

// True when the organizer is on a sales custom contract with Eventbrite
export const isSalesClient = (state: CheckoutState) =>
    !!getNamespaceValue(state.event.properties, 'isSalesClient');

export const isSeries = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'isSeries');

export const isSeriesParent = (state: CheckoutState) =>
    !!getNamespaceValue(state.event, 'isSeriesParent');

export const getIsNosSourceOfTruth = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'nosRollout.isNosSourceOfTruth', false);

export const getShouldAcceptOffline = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'paymentOptions.shouldAcceptOffline') === 1;

export const getShippingRates = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'shippingRates');

export const getHasTaxCountryExemptions = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'hasTaxCountryExemptions') ?? false;

export const getEventSessionsByMonth = (state: CheckoutState) =>
    getNamespaceValue(state.event, 'eventDates');

export const getPublishedDateInEpoch = (state: CheckoutState) => {
    const publishedDatetime = getNamespaceValue(state.event, 'published');

    if (!publishedDatetime) {
        return undefined;
    }

    return new Date(publishedDatetime).getTime();
};
